import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { Badge, Box, Button, Container, Fab, Paper } from "@mui/material";
import { ListReceipts } from "../../../../domain/usecases/list-receipts";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/pt-br";
import { Dayjs } from "dayjs";
import { LoadProductListByManyCodes } from "../../../../domain/usecases/load-product-list-by-many-codes";
import { useErrorHandler } from "../../hooks";
import {
  Receipt,
  ReceiptStatus,
} from "../../../../domain/models/receipt-model";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import { CustomDialog } from "../../../components/Dialog";
import { DeleteReceipt } from "../../../../domain/usecases/delete-receipt";

const columns: GridColDef[] = [
  { field: "id", headerName: "ID", width: 90 },
  {
    field: "receipt",
    headerName: "Recebimento",
    width: 200,
  },
  {
    field: "supplier",
    headerName: "Fornecedor",
    width: 500,
  },
  {
    field: "productDescription",
    headerName: "Produto",
    width: 500,
  },
  {
    field: "batch",
    headerName: "Lote",
    width: 120,
  },
  {
    field: "amount",
    headerName: "Quantidade",
    width: 190,
    valueGetter: (params: GridValueGetterParams) =>
      `${(params.row.amount / (params.row.packageAmount || 1)).toFixed(2)} ${
        params.row.unit
      }`,
  },
  {
    field: "ean",
    headerName: "EAN",
    width: 130,
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
  },
];

type ProductTableRow = {
  id: string;
  receipt: string;
  nf: string;
  supplier: string;
  productDescription: string;
  amount: number;
  batch: string;
  unit: string;
  ean: string;
  status: string;
};

type Props = {
  listReceipts: ListReceipts;
  loadProductByManyCodes: LoadProductListByManyCodes;
  deleteReceipt: DeleteReceipt;
};

export const ReceiptsMain = ({
  listReceipts,
  loadProductByManyCodes,
  deleteReceipt,
}: Props) => {
  const [data, setData] = useState<
    Array<ProductTableRow & { receiptId: number }>
  >([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [toBeDeleteId, setToBeDeleteId] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [date, setDate] = React.useState<Dayjs | null>(null);
  const navigate = useNavigate();

  const handleError = useErrorHandler((error) => {
    setData([]);
  });

  useEffect(() => {
    async function getReceipts() {
      const params = date
        ? { date: date.toISOString() }
        : {
            status: [
              ReceiptStatus.notChecked,
              ReceiptStatus.missingProduct,
              ReceiptStatus.pending,
              ReceiptStatus.unexpectedProduct,
            ],
          };
      const warehouseString = localStorage.getItem("currentWarehouse");
      const warehouse = warehouseString
        ? (JSON.parse(warehouseString) as {
            id: number;
            name: string;
          } | null)
        : undefined;
      try {
        const receipts = await listReceipts.list(warehouse!.id, params);
        if (!receipts) {
          return setData([]);
        }

        if (receipts.length === 0) {
          setReceipts(receipts);
          return setData([]);
        }
        const codes: string[] = [];
        receipts.forEach((receipt) => {
          receipt.receiptCheck?.receiptCheckItems.forEach((item) => {
            codes.push(item.code);
          });
        });
        setReceipts(receipts);
        const products = await loadProductByManyCodes.load(codes);

        const data: Array<ProductTableRow & { receiptId: number }> = [];
        receipts.forEach((receipt, index) => {
          receipt.receiptCheck?.receiptCheckItems.forEach((item, itemIndex) => {
            let row: Partial<ProductTableRow & { receiptId: number }> = {};
            row.id = `${index}${itemIndex}`;
            row.receipt = receipt.name;
            row.supplier = item.supplier;
            row.productDescription = products.find(
              (product) => product.eanCode === item.code
            )?.description;
            row.amount = item.referenceAmount;
            row.unit = item.unit;
            row.ean = item.code;
            row.status = receipt.status;
            row.batch = item.batch;
            row.receiptId = receipt.id;
            data.push(row as ProductTableRow & { receiptId: number });
          });
        });

        setData(data);
      } catch (error: any) {
        handleError(error);
      }
    }
    getReceipts();
  }, [date]);

  return (
    <>
      <CustomDialog
        title="Tem certeza que deseja deletar esse recebimento?"
        content="Ao deletar, qualquer conferência que já tenha sido iniciada será perdida."
        isOpen={isDeleteDialogOpen}
        yesText="Deletar"
        noText="Cancelar"
        onNo={() => {
          setIsDeleteDialogOpen(false);
        }}
        handleClose={() => {
          setIsDeleteDialogOpen(false);
        }}
        onYes={async () => {
          setIsLoading(true);
          try {
            if (toBeDeleteId) {
              await deleteReceipt.delete(toBeDeleteId!);
              setReceipts((old) => {
                return old.filter((receipt) => receipt.id !== toBeDeleteId);
              });
              setData((old) => {
                return old.filter((row) => row.receiptId !== toBeDeleteId);
              });
              setIsDeleteDialogOpen(false);
            }
          } catch (error) {
            alert("Não foi possível deletar o recebimento.");
          } finally {
            setIsLoading(false);
          }
        }}
        isLoading={isLoading}
      />
      <Box
        sx={{
          my: "50px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Container
          sx={{ display: "flex", justifyContent: "space-between", my: "50px" }}
        >
          <div>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-br"
            >
              <DatePicker
                slotProps={{ textField: { size: "small" } }}
                value={date}
                onChange={(newValue) => {
                  if (newValue) {
                    setDate(newValue);
                  }
                }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              sx={{ ml: "10px" }}
              onClick={() => {
                setDate(null);
              }}
            >
              Limpar
            </Button>
          </div>
          <Fab
            color="primary"
            onClick={() => {
              navigate("/receipts/new");
            }}
          >
            <AddIcon />
          </Fab>
        </Container>
        {
          <Container sx={{ width: "100%", my: 2 }}>
            <div>
              {receipts.map((receipt) => {
                return (
                  <Badge
                    key={receipt.id}
                    badgeContent={
                      <DeleteRounded
                        sx={{ fontSize: "16px", cursor: "pointer" }}
                        onClick={() => {
                          setToBeDeleteId(receipt.id!);
                          setIsDeleteDialogOpen(true);
                        }}
                      />
                    }
                    sx={{ my: "10px", mx: "10px" }}
                    color="primary"
                  >
                    <Paper sx={{ padding: "15px", width: "fit-content" }}>
                      {receipt.name}
                    </Paper>
                  </Badge>
                );
              })}
            </div>
          </Container>
        }
        {data.length > 0 && (
          <DataGrid
            rows={data}
            columns={columns}
            sx={{ width: "90%" }}
            slots={{
              toolbar: GridToolbar,
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  supplier: false,
                },
              },
            }}
          />
        )}
      </Box>
    </>
  );
};
